import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { BarChart3, Key, Loader2, Shield, Users, Zap } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useLocation } from "wouter";
import { useEffect } from "react";

const ADMIN_EMAILS = ["florisfok5@gmail.com", "floris.fok@prosus.com"];

const TIER_COLORS: Record<string, string> = {
  free: "var(--muted-foreground)",
  pro: "#eab308",
  max: "#a855f7",
};

function StatCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  description?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      setLocation("/secrets");
    }
  }, [authLoading, isAdmin, setLocation]);

  const { data: stats, isLoading } = trpc.traction.stats.useQuery(undefined, {
    enabled: !!isAdmin,
  });

  if (authLoading || (!isAdmin && !authLoading)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Traction Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Growth metrics and platform health.
          </p>
        </div>

        {isLoading || !stats ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={Users}
              />
              <StatCard
                title="Active Users (7d)"
                value={stats.activeUsers7d}
                icon={Users}
                description={`${stats.activeUsers30d} in last 30 days`}
              />
              <StatCard
                title="Total Secrets"
                value={stats.totalSecrets}
                icon={Key}
              />
              <StatCard
                title="Total Tokens"
                value={stats.totalTokens}
                icon={Shield}
              />
              <StatCard
                title="Active Tokens"
                value={stats.activeTokens}
                icon={Zap}
                description="Not yet expired"
              />
            </div>

            {/* Signups over time chart */}
            {stats.signupsByDay.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    User Signups (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.signupsByDay}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-border"
                        />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(v: string) => {
                            const d = new Date(v);
                            return `${d.getMonth() + 1}/${d.getDate()}`;
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          labelFormatter={(v: string) =>
                            new Date(v).toLocaleDateString()
                          }
                        />
                        <Bar
                          dataKey="count"
                          name="Signups"
                          fill="var(--primary)"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tier distribution */}
            {stats.tierDistribution.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Tier Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-8">
                    <div className="h-48 w-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stats.tierDistribution}
                            dataKey="count"
                            nameKey="tier"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            strokeWidth={2}
                          >
                            {stats.tierDistribution.map((entry) => (
                              <Cell
                                key={entry.tier}
                                fill={TIER_COLORS[entry.tier] ?? "#888"}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {stats.tierDistribution.map((entry) => (
                        <div
                          key={entry.tier}
                          className="flex items-center gap-2 text-sm"
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor:
                                TIER_COLORS[entry.tier] ?? "#888",
                            }}
                          />
                          <span className="capitalize font-medium">
                            {entry.tier}
                          </span>
                          <span className="text-muted-foreground">
                            {entry.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
